import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import {
  HeroBackground,
  CartWidget,
  Pagination,
  PageHeader,
} from "../components";
import { useCart, formatBookPrice, isHorsSerie } from "../context";
import { apiService } from "../services";
import { Book } from "../types";

const ITEMS_PER_PAGE = 12;

interface EditionIntroItem {
  label: string;
  text: string;
}

/**
 * Les titres du catalogue suivent la convention "Titre français / Titl brezhoneg".
 * On isole le titre breton (mis en avant) du titre français (discret).
 */
const splitTitle = (title: string): { breton: string; french?: string } => {
  const separatorIndex = title.indexOf(" / ");
  if (separatorIndex === -1) return { breton: title };
  return {
    french: title.slice(0, separatorIndex).trim(),
    breton: title.slice(separatorIndex + 3).trim(),
  };
};

const BookCard: React.FC<{ book: Book }> = ({ book }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isInCart, toggleItem } = useCart();
  const selected = isInCart(book);
  const isSpecial = isHorsSerie(book);
  const { breton, french } = splitTitle(book.title);

  return (
    <div
      onClick={() => navigate(`/edition/${book.id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(`/edition/${book.id}`);
        }
      }}
      className={`bg-white rounded-lg shadow-md overflow-hidden flex flex-col transition-shadow hover:shadow-xl cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500 ${
        selected ? "ring-2 ring-amber-500" : ""
      }`}
    >
      {/* Couverture */}
      <div className="relative h-56 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
        {book.cover_image ? (
          <img
            src={apiService.getBookCoverUrl(book.id)}
            alt={book.title}
            loading="lazy"
            className="h-full w-full object-contain p-2"
          />
        ) : (
          <svg
            className="w-20 h-20 text-slate-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            />
          </svg>
        )}
        {isSpecial && (
          <span className="absolute top-2 left-2 bg-rose-600 text-white text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full shadow">
            {t("edition.sections.special")}
          </span>
        )}
        {/* Prix */}
        <span className="absolute top-2 right-2 bg-amber-600 text-white text-sm font-bold px-3 py-1 rounded-full shadow">
          {formatBookPrice(book)}
        </span>
      </div>

      {/* Infos */}
      <div className="p-4 flex flex-col flex-1">
        {!isSpecial && (
          <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1">
            N° {book.edition_num}
          </span>
        )}
        <h3 className="text-lg font-semibold text-gray-800 leading-snug line-clamp-2">
          {breton}
        </h3>
        {french && (
          <p className="text-xs text-gray-400 leading-snug mt-0.5">{french}</p>
        )}

        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleItem(book);
          }}
          className={`mt-4 w-full py-2 rounded-lg text-sm font-medium transition inline-flex items-center justify-center gap-1.5 ${
            selected
              ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
              : "bg-amber-600 text-white hover:bg-amber-700"
          }`}
        >
          {selected ? (
            <>
              <Check className="w-4 h-4" /> Dans le panier
            </>
          ) : (
            "Ajouter au panier"
          )}
        </button>
      </div>
    </div>
  );
};

const Grid: React.FC<{ books: Book[] }> = ({ books }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
    {books.map((book) => (
      <BookCard key={book.id} book={book} />
    ))}
  </div>
);

type EditionTab = "presentation" | "purchase" | "list";
type SortColumn = "num" | "title" | "price" | "date";
type SortDirection = "asc" | "desc";

const TABS: EditionTab[] = ["presentation", "purchase", "list"];

const collator = new Intl.Collator("fr", {
  numeric: true,
  sensitivity: "base",
});

export const EditionPage: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<EditionTab>("presentation");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<SortColumn>("num");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const [editions, setEditions] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const data = await apiService.getBooks(0, 1000, true);
        if (!cancelled) setEditions(data);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError(t("edition.load_error"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const handleSort = (column: SortColumn) => {
    if (column === sortColumn) {
      setSortDirection((dir) => (dir === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Onglet « Achat » : uniquement les éditions avec un prix.
  const saleBooks = useMemo(
    () => editions.filter((b) => b.price != null),
    [editions],
  );

  const sortedListRows = useMemo(() => {
    const rows = [...editions];
    rows.sort((a, b) => {
      let result: number;
      if (sortColumn === "price") {
        result = Number(a.price ?? -1) - Number(b.price ?? -1);
      } else if (sortColumn === "date") {
        result = Number(a.year ?? -1) - Number(b.year ?? -1);
      } else if (sortColumn === "num") {
        result = collator.compare(a.edition_num ?? "", b.edition_num ?? "");
      } else {
        result = collator.compare(a.title, b.title);
      }
      return sortDirection === "asc" ? result : -result;
    });
    return rows;
  }, [editions, sortColumn, sortDirection]);

  const filteredBooks = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return saleBooks;
    return saleBooks.filter(
      (book) =>
        book.title.toLowerCase().includes(query) ||
        (book.edition_num
          ? book.edition_num.toLowerCase().includes(query)
          : false),
    );
  }, [saleBooks, search]);

  const totalPages = Math.ceil(filteredBooks.length / ITEMS_PER_PAGE);
  const paginatedBooks = filteredBooks.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE,
  );

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const introParagraph2 = t("edition.intro.paragraph2");
  const introItems = t("edition.intro.items", {
    returnObjects: true,
  }) as EditionIntroItem[];

  return (
    <div className="h-full flex flex-col">
      <HeroBackground size="third" />

      {/* Titre */}
      <div className="flex-shrink-0">
        <PageHeader
          title={t("edition.intro.heading")}
          subtitle={t("subtitles.edition")}
        />
      </div>

      <div className="max-w-6xl w-full mx-auto px-5 pb-8 flex-1 min-h-0">
        <div className="flex flex-col md:flex-row gap-6 h-full">
          {/* Barre de gauche : onglets + recherche, fixe */}
          <aside className="md:w-64 flex-shrink-0 bg-church-light  rounded-lg shadow-sm p-4">
            <nav className="flex md:flex-col gap-2">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`text-left px-4 py-2.5 rounded-lg text-sm font-medium transition whitespace-nowrap ${
                    activeTab === tab
                      ? "bg-amber-600 text-white shadow"
                      : "text-gray-700 hover:bg-amber-50"
                  }`}
                >
                  {t(`edition.tabs.${tab}`)}
                </button>
              ))}
            </nav>

            {activeTab === "purchase" && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="relative">
                  <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-4.35-4.35m1.85-5.15a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder={t("edition.search_placeholder")}
                    className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-300 bg-white text-gray-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>
            )}
          </aside>

          {/* Contenu de l'onglet : seul élément qui doit scroller */}
          <div className="flex-1 min-w-0 h-full overflow-y-auto rounded-lg p-1 md:p-2">
            {activeTab === "presentation" && (
              <section className="bg-church-light rounded-lg shadow-sm p-6 md:p-10 prose max-w-none">
                <p className="text-gray-700 leading-relaxed">
                  {t("edition.intro.paragraph1")}
                </p>

                {!!introParagraph2 && (
                  <>
                    <p className="text-gray-700 leading-relaxed mt-4">
                      {introParagraph2}
                    </p>
                    <ul className="mt-3 space-y-2 text-gray-700">
                      {introItems.map((item, i) => (
                        <li key={i}>
                          <strong>{item.label}</strong>
                          {item.text}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </section>
            )}

            {error && activeTab !== "presentation" && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}

            {loading && activeTab !== "presentation" && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600"></div>
              </div>
            )}

            {activeTab === "purchase" && !loading && (
              <>
                {/* Ouvrages en vente */}
                <section>
                  {paginatedBooks.length > 0 ? (
                    <Grid books={paginatedBooks} />
                  ) : (
                    <p className="text-center text-gray-500">
                      {t("edition.no_results")}
                    </p>
                  )}
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                  />
                </section>

                {/* Bon de commande */}
                <div className="mt-14 text-center text-gray-600 text-sm">
                  <p>{t("edition.order_note")}</p>
                </div>
              </>
            )}

            {activeTab === "list" && !loading && (
              <section className="bg-white rounded-lg shadow-sm h-full overflow-auto">
                <table className="w-full text-left text-sm border-collapse">
                  <thead className="bg-slate-50 text-gray-600 uppercase text-xs tracking-wide">
                    <tr>
                      {(
                        [
                          ["num", t("edition.list.number")],
                          ["title", t("edition.list.title")],
                          ["date", t("edition.list.date")],
                          ["price", t("edition.list.price")],
                        ] as [SortColumn, string][]
                      ).map(([col, label]) => (
                        <th
                          key={col}
                          onClick={() => handleSort(col)}
                          className="sticky top-0 z-10 bg-slate-50 px-4 py-3 font-semibold cursor-pointer select-none whitespace-nowrap"
                        >
                          {label}
                          {sortColumn === col && (
                            <span className="ml-1">
                              {sortDirection === "asc" ? "▲" : "▼"}
                            </span>
                          )}
                        </th>
                      ))}
                      <th className="sticky top-0 z-10 bg-slate-50 px-4 py-3 font-semibold whitespace-nowrap">
                        {t("edition.list.link")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sortedListRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-8 text-center text-gray-400"
                        >
                          {t("edition.list.empty")}
                        </td>
                      </tr>
                    ) : (
                      sortedListRows.map((row) => (
                        <tr key={row.id}>
                          <td className="px-4 py-3">
                            {isHorsSerie(row) ? (
                              <span className="text-rose-600 font-medium">
                                {t("edition.sections.special")}
                              </span>
                            ) : (
                              row.edition_num
                            )}
                          </td>
                          <td className="px-4 py-3">{row.title}</td>
                          <td className="px-4 py-3">{row.year ?? ""}</td>
                          <td className="px-4 py-3">{formatBookPrice(row)}</td>
                          <td className="px-4 py-3">
                            {row.external_link ? (
                              <a
                                href={row.external_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-amber-700 hover:underline"
                              >
                                {t("edition.list.link")}
                              </a>
                            ) : (
                              ""
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </section>
            )}
          </div>
        </div>
      </div>

      <CartWidget />
    </div>
  );
};
